import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import DataTable, { type ExpanderComponentProps, type TableColumn } from "react-data-table-component";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import { BedrockEstimatePanel } from "../../components/admin/BedrockEstimatePanel";
import { ObservationCleanUpPanel } from "../../components/admin/ObservationCleanUpPanel";
import { SyncSchedulePanel } from "../../components/admin/SyncSchedulePanel";
import { AdminGate } from "../../components/admin/AdminGate";
import { Modal } from "../../components/admin/Modal";
import { fetchProviderWriteEnable, updateProviderWriteEnable, type ProviderWriteEnable } from "../../api/providerWriteEnable";
import { fetchWriteSafetySetting } from "../../api/writeSafety";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import { RelativeTimestamp } from "../../components/RelativeTimestamp";
import styles from "../../components/admin/Admin.module.css";
import { fetchSyncLastRuns, fetchSyncStatus, rebuildSync, runFullSync, runSyncNow, type SyncRunDetail, type VendorLastRun, type VendorSyncStatus } from "../../api/syncStatus";
import { useSession } from "../../session/SessionContext";
import { useToast } from "../../components/toast/toastContext";

/**
 * Every administration permission this phase's screens declare. `GET /api/status/sync` is itself
 * `AllowAnonymousAccess` (`StatusEndpoints`, `TD-30`) — it carries no per-person data, so the API
 * allows an unauthenticated caller. This screen does not rely on that: it gates on holding *any*
 * administration permission, so an unclassified visitor who happens to reach this route is still
 * shown the denied state, never the vendor sync table, purely because of what the route itself
 * requires — independent of, and stricter than, what the one API call behind it would allow.
 */
const AnyAdminPermission = ["entitlement.administer", "roles.administer", "accounts.administer", "audit.read", "request.approve", "settings.write"];

/**
 * The "Sync status" screen (phase 21, `AIA-81`, `TD-30`): last successful sync, last attempt and
 * next expected refresh, per vendor.
 */
export function SyncStatusPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <AdminGate permissions={AnyAdminPermission} mode="any" title={t("admin.syncStatus.denied.title")} description={t("admin.syncStatus.denied.description")}>
        <SyncStatusContent />
      </AdminGate>
    </div>
  );
}

function ResyncDialog({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
    dialogRef.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby="resync-heading"
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <div className={styles.dialogBody}>
        <h2 id="resync-heading">{t("admin.syncStatus.resyncTitle")}</h2>
        <p>{t("admin.syncStatus.resyncWarning")}</p>
        <div className={styles.syncActions}>
          <button type="button" className="button button-secondary" onClick={onCancel}>
            {t("admin.syncStatus.cancel")}
          </button>
          <button type="button" className="button button-primary" data-autofocus onClick={onConfirm}>
            {t("admin.syncStatus.ok")}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function toLastRunMap(runs: VendorLastRun[]): Record<string, SyncRunDetail | null> {
  return Object.fromEntries(runs.map((run) => [run.vendorId, run.lastAttempt ?? null]));
}

/** The sub-row under a vendor: how its most recent sync attempt went, or why it failed. */
function SyncRunDetails({ data }: ExpanderComponentProps<SyncStatusRow>): React.JSX.Element {
  const { t } = useTranslation();

  if (!data.runDetailsAvailable) {
    return <p className={styles.syncRunDetails}>{t("admin.syncStatus.runDetails.unavailable")}</p>;
  }
  const run = data.lastRun;
  if (run === null) {
    return <p className={styles.syncRunDetails}>{t("admin.syncStatus.runDetails.none")}</p>;
  }

  const failed = run.outcome === "Failed";
  return (
    <dl className={styles.syncRunDetails}>
      <div>
        <dt>{t("admin.syncStatus.runDetails.changed")}</dt>
        <dd>
          {run.changedCount}
          {run.wasDryRun && ` (${t("admin.syncStatus.runDetails.dryRun")})`}
        </dd>
      </div>
      {failed && (
        <div className={styles.syncRunDetailsWide}>
          <dt>{t("admin.syncStatus.runDetails.failureReason")}</dt>
          <dd>
            <pre className={styles.syncFailureDetail}>{run.failureDetail ?? t("admin.syncStatus.runDetails.noFailureDetail")}</pre>
          </dd>
        </div>
      )}
    </dl>
  );
}

/**
 * One table row: a vendor's status plus its last sync attempt. `runDetailsAvailable` is false when
 * the caller may not read administration-only run details, so the sub-row can say so rather than
 * showing "never attempted" for a vendor that has in fact run.
 */
interface SyncStatusRow extends VendorSyncStatus {
  lastRun: SyncRunDetail | null;
  runDetailsAvailable: boolean;
}

export function SyncStatusContent(): React.JSX.Element {
  const toast = useToast();
  const { t } = useTranslation();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "loaded"; vendors: VendorSyncStatus[]; lastRuns: Record<string, SyncRunDetail | null> | null }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  const [syncing, setSyncing] = useState<string | null>(null);
  // The open "more sync options" menu and where to draw it, and which vendor's resync warning is showing. The menu is drawn outside the table,
  // because the table's wrapper clips anything that runs past its bottom edge.
  const [menu, setMenu] = useState<{ vendorId: string; top: number; right: number } | null>(null);
  const [resyncVendor, setResyncVendor] = useState<string | null>(null);

  useEffect(() => {
    if (menu === null) {
      return undefined;
    }

    const close = () => setMenu(null);
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target.closest("[data-sync-menu]") === null && target.closest("[data-sync-caret]") === null) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu]);
  const { snapshot } = useSession();
  const canSyncNow = snapshot.status === "signed-in" && snapshot.permissions.includes("settings.write");

  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setState({ kind: "loading" });
    }
    // Run details are read separately: a person without settings.write still sees the status table,
    // with the sub-row saying the details are unavailable to them.
    const [result, runsResult] = await Promise.all([fetchSyncStatus(), fetchSyncLastRuns()]);
    if (result.kind === "ok") {
      setState({ kind: "loaded", vendors: result.response, lastRuns: runsResult.kind === "ok" ? toLastRunMap(runsResult.response) : null });
    } else if (!silent && result.kind === "denied") {
      setState({ kind: "error", message: t("admin.syncStatus.denied.description") });
    } else if (!silent && result.kind === "error") {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Write-enable switches (AIA-109 phase 7). Read once on entry, and again only after a save: they change rarely, and the 3-second poll
  // above is for sync progress. Visible only to a person who may change them; otherwise the column stays empty. A failed read hides the
  // switches and leaves the table usable; it is not reported inline.
  const [writes, setWrites] = useState<ProviderWriteEnable[] | null>(null);
  const [killSwitchOn, setKillSwitchOn] = useState(false);
  const [writePending, setWritePending] = useState<string | null>(null);
  const [confirmEnable, setConfirmEnable] = useState<ProviderWriteEnable | null>(null);

  const loadWrites = useCallback(async () => {
    const [providers, safety] = await Promise.all([fetchProviderWriteEnable(), fetchWriteSafetySetting()]);
    if (providers.kind === "ok") {
      setWrites(providers.response);
    }
    setKillSwitchOn(safety.kind === "ok" && safety.response.killSwitchEnabled);
  }, []);

  useEffect(() => {
    if (canSyncNow) {
      void loadWrites();
    }
  }, [canSyncNow, loadWrites]);

  const saveWriteEnable = async (vendor: ProviderWriteEnable, enabled: boolean): Promise<void> => {
    setWritePending(vendor.vendorId);
    const result = await updateProviderWriteEnable(vendor.vendorId, enabled);
    setWritePending(null);
    if (result.kind === "ok") {
      const saved = result.response;
      setWrites((current) => current?.map((candidate) => (candidate.vendorId === saved.vendorId ? saved : candidate)) ?? null);
      toast.show(t(enabled ? "admin.syncStatus.writes.enabledToast" : "admin.syncStatus.writes.disabledToast", { vendor: vendor.displayName }), "success");
    } else {
      toast.show(result.kind === "denied" ? t("admin.syncStatus.denied.description") : result.message, "error");
    }
  };

  // Enabling makes real changes at the vendor, so it asks first; disabling only moves the vendor back to simulated writes, so it does not.
  const onWriteSwitch = (vendor: ProviderWriteEnable, enabled: boolean): void => {
    if (enabled) {
      setConfirmEnable(vendor);
    } else {
      void saveWriteEnable(vendor, false);
    }
  };

  const anyRunning = state.kind === "loaded" && state.vendors.some((vendor) => vendor.isRunning);
  const awaitingStart = syncing !== null;
  const shouldPoll = anyRunning || awaitingStart;

  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }
    const timer = window.setInterval(() => void load(true), 3000);
    return () => window.clearInterval(timer);
  }, [shouldPoll, load]);

  useEffect(() => {
    if (syncing === null || state.kind !== "loaded") {
      return undefined;
    }
    const vendor = state.vendors.find((candidate) => candidate.vendorId === syncing);
    if (vendor?.isRunning) {
      setSyncing(null);
      return undefined;
    }
    // The background run may be declined (lease held) or finish between polls; stop waiting after a while.
    const timeout = window.setTimeout(() => setSyncing(null), 15000);
    return () => window.clearTimeout(timeout);
  }, [syncing, state]);

  const triggerSync = async (vendorId: string, full: boolean): Promise<void> => {
    setSyncing(vendorId);
    const result = full ? await runFullSync(vendorId) : await runSyncNow(vendorId);
    if (result.kind === "ok") {
      toast.show(t(full ? "admin.syncStatus.syncAllStarted" : "admin.syncStatus.syncNowStarted"), "success");
      void load(true);
    } else {
      setSyncing(null);
      toast.show(result.kind === "denied" ? t("admin.syncStatus.denied.description") : result.message, "error");
    }
  };

  const triggerRebuild = async (vendorId: string): Promise<void> => {
    setSyncing(vendorId);
    const result = await rebuildSync(vendorId);
    if (result.kind === "ok") {
      toast.show(t("admin.syncStatus.rebuildStarted"), "success");
      void load(true);
    } else {
      setSyncing(null);
      toast.show(result.kind === "denied" ? t("admin.syncStatus.denied.description") : result.message, "error");
    }
  };

  const rows = useMemo<SyncStatusRow[]>(
    () =>
      state.kind === "loaded"
        ? state.vendors.map((vendor) => ({
            ...vendor,
            lastRun: state.lastRuns?.[vendor.vendorId] ?? null,
            runDetailsAvailable: state.lastRuns !== null,
          }))
        : [],
    [state],
  );

  const columns = useMemo<TableColumn<SyncStatusRow>[]>(
    () => [
      { id: "vendor", name: t("admin.syncStatus.table.vendor"), selector: (row) => row.displayName, sortable: true },
      {
        id: "lastSuccess",
        name: t("admin.syncStatus.table.lastSuccess"),
        selector: (row) => row.lastSuccessfulSyncAt ?? "",
        sortable: true,
        cell: (row) => (row.hasSyncedBefore ? <RelativeTimestamp isoTimestamp={row.lastSuccessfulSyncAt} /> : t("admin.syncStatus.neverSynced")),
      },
      {
        id: "lastAttempt",
        name: t("admin.syncStatus.table.lastAttempt"),
        selector: (row) => row.lastAttemptAt ?? "",
        sortable: true,
        cell: (row) => (row.lastAttemptAt ? <RelativeTimestamp isoTimestamp={row.lastAttemptAt} /> : t("admin.syncStatus.neverAttempted")),
      },
      {
        id: "outcome",
        name: t("admin.syncStatus.table.outcome"),
        selector: (row) => row.lastAttemptOutcome ?? "",
        sortable: true,
        cell: (row) => row.lastAttemptOutcome ?? t("admin.syncStatus.noOutcomeYet"),
      },
      {
        id: "nextExpected",
        name: t("admin.syncStatus.table.nextExpected"),
        selector: (row) => row.nextExpectedRefreshAt,
        sortable: true,
        cell: (row) => <RelativeTimestamp isoTimestamp={row.nextExpectedRefreshAt} />,
      },
      {
        id: "writes",
        name: t("admin.syncStatus.table.writes"),
        cell: (row) => {
          // The sync status names a vendor as VendorId.ToString() ("Bedrock"); the write-enable list uses the lower-case key ("bedrock").
          const provider = writes?.find((candidate) => candidate.vendorId.toLowerCase() === row.vendorId.toLowerCase());
          if (provider === undefined) {
            return null;
          }
          const blockedByKillSwitch = provider.hasWriter && provider.writesEnabled && killSwitchOn;
          const note = !provider.hasWriter
            ? t("admin.syncStatus.writes.readOnly")
            : blockedByKillSwitch
              ? t("admin.syncStatus.writes.blockedByKillSwitch")
              : t(provider.writesEnabled ? "admin.syncStatus.writes.on" : "admin.syncStatus.writes.off");
          return (
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: 0, cursor: provider.hasWriter ? "pointer" : "default" }}>
              <input
                type="checkbox"
                role="switch"
                className={styles.allocationToggle}
                aria-label={t("admin.syncStatus.writes.switchLabel", { vendor: provider.displayName })}
                checked={provider.writesEnabled}
                disabled={!provider.hasWriter || writePending === provider.vendorId}
                onChange={(event) => onWriteSwitch(provider, event.target.checked)}
              />
              <span>{note}</span>
            </label>
          );
        },
        minWidth: "220px",
      },
      {
        id: "actions",
        name: t("admin.syncStatus.table.actions"),
        cell: (row) => {
          if (!canSyncNow) {
            return null;
          }
          const busy = row.isRunning || syncing === row.vendorId;
          return (
            <div className={styles.syncActions}>
              <div className={styles.syncSplit}>
                <button type="button" className={`button button-primary ${styles.syncSplitMain}`} disabled={busy} onClick={() => void triggerSync(row.vendorId, false)}>
                  {busy ? t("admin.syncStatus.syncing") : t("admin.syncStatus.syncNow")}
                </button>
                <div className={styles.syncMenuAnchor}>
                  <button
                    type="button"
                    data-sync-caret=""
                    className={`button button-primary ${styles.syncSplitCaret}`}
                    disabled={busy}
                    aria-haspopup="menu"
                    aria-expanded={menu?.vendorId === row.vendorId}
                    aria-label={t("admin.syncStatus.moreSyncOptions", { vendor: row.displayName })}
                    onClick={(event) => {
                      const rect = event.currentTarget.getBoundingClientRect();
                      setMenu(menu?.vendorId === row.vendorId ? null : { vendorId: row.vendorId, top: rect.bottom + 4, right: window.innerWidth - rect.right });
                    }}
                  >
                    {t("admin.syncStatus.menuCaret")}
                  </button>
                </div>
              </div>
            </div>
          );
        },
        minWidth: "260px",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canSyncNow, syncing, menu?.vendorId, writes, killSwitchOn, writePending],
  );

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.syncStatus.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.syncStatus.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  if (state.vendors.length === 0) {
    return <EmptyState title={t("admin.syncStatus.emptyTitle")} description={t("admin.syncStatus.emptyDescription")} />;
  }

  return (
    <div>
      {killSwitchOn && (
        <div className={`${styles.killSwitchPanel} ${styles.killSwitchPanelActive}`} role="status">
          <p>
            {t("admin.syncStatus.writes.killSwitchBanner")} <Link to="/admin/providers/write-safety">{t("admin.syncStatus.writes.killSwitchLink")}</Link>
          </p>
        </div>
      )}
      {confirmEnable !== null && (
        <Modal title={t("admin.syncStatus.writes.confirmTitle", { vendor: confirmEnable.displayName })} onClose={() => setConfirmEnable(null)}>
          <p>{t("admin.syncStatus.writes.confirmBody", { vendor: confirmEnable.displayName })}</p>
          <div className={styles.syncActions}>
            <button type="button" className="button button-secondary" onClick={() => setConfirmEnable(null)}>
              {t("admin.syncStatus.cancel")}
            </button>
            <button
              type="button"
              className="button button-primary"
              data-autofocus
              onClick={() => {
                const vendor = confirmEnable;
                setConfirmEnable(null);
                void saveWriteEnable(vendor, true);
              }}
            >
              {t("admin.syncStatus.writes.confirmAction")}
            </button>
          </div>
        </Modal>
      )}
      {menu !== null &&
        createPortal(
          <div role="menu" data-sync-menu="" className={styles.syncMenu} style={{ top: menu.top, right: menu.right }}>
            <button
              type="button"
              role="menuitem"
              className={styles.syncMenuItem}
              onClick={() => {
                const vendorId = menu.vendorId;
                setMenu(null);
                void triggerSync(vendorId, true);
              }}
            >
              {t("admin.syncStatus.syncAll")}
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.syncMenuItem}
              onClick={() => {
                const vendorId = menu.vendorId;
                setMenu(null);
                setResyncVendor(vendorId);
              }}
            >
              {t("admin.syncStatus.resyncEverything")}
            </button>
          </div>,
          document.body,
        )}
      {resyncVendor !== null && (
        <ResyncDialog
          onCancel={() => setResyncVendor(null)}
          onConfirm={() => {
            const vendorId = resyncVendor;
            setResyncVendor(null);
            void triggerRebuild(vendorId);
          }}
        />
      )}
      <div className={`${styles.tableWrapper} ${styles.peopleTableWrapper} ${styles.syncTableWrapper}`}>
        <DataTable
          columns={columns}
          data={rows}
          className={styles.peopleDataTable}
          keyField="vendorId"
          expandableRows
          expandableRowsComponent={SyncRunDetails}
          ariaLabel={t("admin.nav.syncStatus")}
          theme={adminTableTheme}
          striped
          highlightOnHover
          columnSeparator="subtle"
        />
      </div>
      {canSyncNow && <SyncSchedulePanel />}
      {canSyncNow && state.vendors.some((vendor) => vendor.vendorId.toLowerCase() === "bedrock") && <BedrockEstimatePanel />}
      {canSyncNow && <ObservationCleanUpPanel />}
    </div>
  );
}
